import {FC, useState} from 'react';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {AiAgentChartData, AiAgentPdfExport} from 'src/types';
import {CHART_METRICS} from './ChatChart';
import {ChartExportRig} from './ChartExportRig';
import {captureCharts, captureExportCharts} from '../utils/chartCapture';
import {ExportMessage, PerMetricImages, SingleChartImages, collectExportContent} from '../utils/collectExportContent';
import {generatePdf} from '../utils/generatePdf';
import {downloadPdf} from '../utils/pdfDownload';
import {PdfMeta} from '../utils/pdfTypes';

const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

// Wait until the off-screen export rig has rendered its chart SVGs (or a ~2s cap),
// then one more frame so recharts has painted — before capturing.
const waitForRigCharts = async (expected: number): Promise<void> => {
    for (let i = 0; i < 40; i++) {
        if (document.querySelectorAll('[data-ai-export-chart] svg').length >= expected) break;
        await new Promise((r) => setTimeout(r, 50));
    }
    await raf();
    await raf();
};

interface PdfExportButtonProps {
    export: AiAgentPdfExport;
    // The full chat state — the button resolves the tables/charts/summary to include
    // per the export's `scope`, so it needs every message, not just its own bubble.
    messages: ExportMessage[];
}

// A "Download PDF" button attached to an assistant message. The PDF is assembled in
// the browser from what's already on screen (Swiss data-sovereignty: nothing leaves
// the client). Persisted with the message, so it survives reloads and is re-clickable.
export const PdfExportButton: FC<PdfExportButtonProps> = ({export: exp, messages}) => {
    const intl = useIntl();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    // Charts to render in the hidden export rig (all metric views), set only during
    // an export so we can capture all 4 metrics per chart, then cleared.
    const [rigCharts, setRigCharts] = useState<AiAgentChartData[] | null>(null);

    const handleClick = async (): Promise<void> => {
        if (busy) return;
        setBusy(true);
        setError(false);
        // Let the "Preparing…" state paint before the (brief) main-thread work of
        // capturing charts / assembling sections; the heavy render runs in a worker.
        await new Promise((r) => setTimeout(r, 0));
        try {
            const fm = (d: {id: string; defaultMessage?: string}) => intl.formatMessage(d);
            let perMetric: PerMetricImages | undefined;
            let single: SingleChartImages | undefined;
            const wantCharts = exp.scope === 'charts' || exp.scope === 'everything';
            const allCharts = wantCharts ? messages.flatMap((m) => m.charts ?? []) : [];
            if (allCharts.length) {
                // Fallback: the on-screen chart image (selected metric) — captured first.
                single = await captureCharts();
                // All-4-metrics: render every chart × metric off-screen, capture each.
                setRigCharts(allCharts);
                await waitForRigCharts(allCharts.length * CHART_METRICS.length);
                perMetric = await captureExportCharts();
                setRigCharts(null);
            }
            const sections = collectExportContent(exp, messages, fm, perMetric, single);
            if (sections.length === 0) {
                setError(true);
                return;
            }
            const title = exp.title?.trim()
                || intl.formatMessage({id: 'aiAgentPdfDefaultTitle', defaultMessage: 'AMP IT Report'});
            const generatedAt = intl.formatMessage(
                {id: 'aiAgentPdfGeneratedAt', defaultMessage: 'Generated {date}'},
                {date: dayjs().format('DD.MM.YYYY HH:mm')},
            );
            const meta: PdfMeta = {
                reportLabel: intl.formatMessage({id: 'aiAgentPdfReportLabel', defaultMessage: 'AI Assistant Report'}),
                subtitle: intl.formatMessage({id: 'aiAgentPdfSubtitle'}),
                generatedAt,
                footerNote: intl.formatMessage({id: 'aiAgentPdfFooter', defaultMessage: 'AMP IT · Confidential'}),
            };
            const blob = await generatePdf(title, sections, meta);
            downloadPdf(title, blob);
        } catch {
            setError(true);
        } finally {
            setRigCharts(null);
            setBusy(false);
        }
    };

    return (
        <Box sx={{mt: 1.5}}>
            {rigCharts && <ChartExportRig charts={rigCharts} />}
            <Button
                variant="outlined"
                size="small"
                startIcon={busy ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon />}
                onClick={handleClick}
                disabled={busy}
                sx={{textTransform: 'none', borderRadius: 2}}
            >
                {intl.formatMessage({id: busy ? 'aiAgentPdfPreparing' : 'aiAgentDownloadPdf'})}
            </Button>
            {error && (
                <Typography variant="caption" color="error" sx={{display: 'block', mt: 0.5}}>
                    {intl.formatMessage({id: 'aiAgentPdfError'})}
                </Typography>
            )}
        </Box>
    );
};
